import { type EditAction } from '@/application';
import { evaluate, type EntitySource } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { asEntityId, createEntity, DEFAULT_STYLE, type EntityId } from '@/domain/model';
import { cornerName, NO_ROTATION, ProjectionKind, SolidKind } from '@/domain/solid';
import { hitTest } from '@/engine';

import { currentSnap, placeAnchor } from './pointFactory';
import {
  type PointerInput,
  type Tool,
  type ToolContext,
  type ToolPrompt,
  type ToolResponse,
} from './Tool';

/**
 * Solid geometry tools.
 *
 * Creating a solid is an ordinary construction: the solid hangs off a point and is derived from
 * it, so it moves when that point moves, appears in the object list, undoes in one step and saves
 * to the file like anything else — none of which needed new machinery.
 *
 * What did need its own tool is the first click. Every other construction picks things that are
 * already there; this one is asked to put a shape *somewhere*, and where is a place rather than an
 * object. `placeAnchor` is where that difference is written down.
 *
 * The one genuinely new gesture is rotation, which has no analogue in the plane.
 */

/** Default extent of a newly placed solid, in world units. */
const DEFAULT_EXTENT = 120;

interface SolidSpec {
  readonly kind: SolidKind;
  readonly id: string;
  readonly label: string;
  readonly shortcut: string;
}

const SOLIDS: readonly SolidSpec[] = [
  { kind: SolidKind.Cube, id: 'solid-cube', label: 'Lập phương', shortcut: '1' },
  { kind: SolidKind.Cuboid, id: 'solid-cuboid', label: 'Hình hộp', shortcut: '2' },
  { kind: SolidKind.Prism, id: 'solid-prism', label: 'Lăng trụ', shortcut: '3' },
  { kind: SolidKind.Pyramid, id: 'solid-pyramid', label: 'Hình chóp', shortcut: '4' },
  { kind: SolidKind.Cylinder, id: 'solid-cylinder', label: 'Hình trụ', shortcut: '5' },
  { kind: SolidKind.Cone, id: 'solid-cone', label: 'Hình nón', shortcut: '6' },
  { kind: SolidKind.Sphere, id: 'solid-sphere', label: 'Hình cầu', shortcut: '7' },
  { kind: SolidKind.Frustum, id: 'solid-frustum', label: 'Nón cụt', shortcut: '8' },
];

export function createSolidTools(): readonly Tool[] {
  return [...SOLIDS.map((spec) => new PlaceSolidTool(spec)), new RotateSolidTool()];
}

/** The solid a spec describes, resting on `center`. */
function solidOn(kind: SolidKind, center: EntityId): EntitySource {
  return {
    op: 'solid',
    solid: kind,
    center,
    width: DEFAULT_EXTENT,
    // A cuboid is only distinguishable from a cube if it starts out non-cubic.
    height: kind === SolidKind.Cuboid ? DEFAULT_EXTENT * 1.4 : DEFAULT_EXTENT,
    depth: kind === SolidKind.Cuboid ? DEFAULT_EXTENT * 0.7 : DEFAULT_EXTENT,
    sides: 6,
    taper: 0.5,
    rotation: NO_ROTATION,
    projection: ProjectionKind.Isometric,
  };
}

/**
 * Puts a solid where the user clicks.
 *
 * A `ConstructionTool` with one `aPoint` step until the report that a solid always had to be
 * built on a point somebody had made first. That machinery picks entities that exist, which is
 * right for the twenty-five tools that use it and wrong for this one: the answer to "where does
 * the cube go" is usually a place, not an object. Bending `hitForCurrentStep` into conjuring its
 * own pick would have made the one abstraction those tools share mean two things.
 *
 * What it keeps from that tool is the part worth keeping. It previews the real construction —
 * evaluating the solid that *would* be created, against the position under the cursor — so the
 * cube is visible before the click rather than after it, and one response carries every action,
 * so the anchor, the solid and its eight named corners are a single undo step.
 */
class PlaceSolidTool implements Tool {
  readonly id: string;
  readonly label: string;
  readonly shortcut: string;
  readonly group = 'Khối';
  private readonly kind: SolidKind;

  constructor(spec: SolidSpec) {
    this.id = spec.id;
    this.label = spec.label;
    this.shortcut = spec.shortcut;
    this.kind = spec.kind;
  }

  prompt(): ToolPrompt | null {
    return {
      tool: this.label,
      step: 0,
      steps: 1,
      hint: 'nơi đặt khối — nhấp vào chỗ trống, hoặc vào một điểm để khối bám theo điểm đó',
    };
  }

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const snapped = currentSnap(context, input);
    const anchor = placeAnchor(context, snapped, input.world);

    const id = context.nextId('sol');
    const source = solidOn(this.kind, anchor.id);

    return {
      actions: [
        ...anchor.actions,
        { kind: 'add-entity', entity: createEntity(id, source, context.style) },
        // After the solid and never before it: they name it, so it has to have an id.
        ...namedCorners(source, id, context),
      ],
      // The solid stays selected, not its anchor and not its corners — a user who has just placed
      // a cube means the cube, and eight corners in the selection would put the wrong panel up.
      selection: [id],
      preview: [],
      snap: snapped,
    };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    const snapped = currentSnap(context, input);

    /*
     * Evaluated against a position rather than an entity, because there is no entity yet and the
     * whole point of the tool is that there need not be one. `evaluate` takes its parents as
     * values, so a point that exists only for the length of this call is a legitimate parent —
     * and the preview is then the *same* computation the click performs, which is what stops the
     * two from disagreeing.
     */
    const value = evaluate(solidOn(this.kind, PREVIEW_ANCHOR), [
      { kind: 'point', point: snapped.world },
    ]);

    return {
      snap: snapped,
      preview: value === null ? [] : [value],
      cursor: 'copy',
    };
  }

  cancel(): ToolResponse | null {
    return null;
  }

  reset(): void {
    /* stateless: one click places a solid, so there is nothing half-done to abandon */
  }
}

/** Never stored. The preview's parent is supplied as a value, so the name is never looked up. */
const PREVIEW_ANCHOR = asEntityId('preview');

/**
 * The corners a solid arrives with, named.
 *
 * ## Why the toolbar names them at all
 *
 * Because the other two paths do. A solid written in the script names every corner, and so does
 * one raised on a drawn base — with the reason stated there: *a solid nobody can point at is a
 * picture, and every problem opens by naming corners.* A cube dropped from the toolbar named
 * none, so the same figure was addressable or not depending on which button made it, and the
 * report was the plainest possible form of that: the points of a solid cannot be named.
 *
 * ## Why only four of the eight
 *
 * A sphere has no corners. A cylinder, a cone and a frustum have a great many, and every one of
 * them is a tessellation artefact — `buildMesh` cuts a circle into `CURVE_SEGMENTS` pieces and
 * the pieces are an implementation detail of drawing a curve, not places in the figure. Lettering
 * them would put thirty-two names round a rim that has none.
 *
 * The four that do have corners are numbered by `buildMesh` in exactly the order `cornerName`
 * expects: the base ring, then the top ring in matching order, with a pyramid's apex last.
 */
function namedCorners(
  source: EntitySource,
  solid: EntityId,
  context: ToolContext
): readonly EditAction[] {
  if (source.op !== 'solid') return [];

  const shape = LETTERED[source.solid];
  if (shape === undefined) return [];

  const base = shape.base ?? Math.max(3, Math.round(source.sides));
  const total = shape.kind === 'pyramid' ? base + 1 : base * 2;

  return Array.from({ length: total }, (_, index) => ({
    kind: 'add-entity' as const,
    entity: {
      ...createEntity(context.nextId('sv'), { op: 'solid-vertex', solid, index }, DEFAULT_STYLE),
      label: cornerName(index, base, shape.kind),
    },
  }));
}

/** The solids whose corners are places in the figure rather than pieces of a drawn curve. */
const LETTERED: Partial<
  Record<SolidKind, { readonly kind: 'prism' | 'pyramid'; readonly base?: number }>
> = {
  [SolidKind.Cube]: { kind: 'prism', base: 4 },
  [SolidKind.Cuboid]: { kind: 'prism', base: 4 },
  [SolidKind.Prism]: { kind: 'prism' },
  [SolidKind.Pyramid]: { kind: 'pyramid' },
};

/* ------------------------------------------------------------ interactive rotation */

type RotateState =
  | { readonly name: 'idle' }
  | {
      readonly name: 'turning';
      readonly id: EntityId;
      readonly from: Vec2;
      readonly start: { x: number; y: number; z: number };
    };

/**
 * Turns a solid by dragging it.
 *
 * Horizontal drag yaws, vertical drag pitches — the mapping every 3D viewer uses, so the
 * gesture is already known before the user tries it. Rotation is about the solid's own
 * axes, not the world's, because the user is turning an object on a table rather than
 * flying a camera around it; that is the mental model a geometry lesson works in.
 *
 * The whole drag is one undo step. Rotation emits an edit on every pointer move, and
 * without a merge key a single turn would fill the history with three hundred entries and
 * make undo useless. `mergeKey` is what the history layer already provides for exactly this.
 */
export class RotateSolidTool implements Tool {
  readonly id = 'rotate-solid';
  readonly label = 'Xoay khối';
  readonly shortcut = '0';
  readonly group = 'Khối';

  private state: RotateState = { name: 'idle' };

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const hit = hitTest(context.scene, input.world, context.tolerance, {
      filter: (_id, kind) => kind === 'solid',
    });
    if (hit === null) return null;

    const entity = context.document.entities.get(hit.id);
    if (entity?.source.op !== 'solid') return null;

    this.state = {
      name: 'turning',
      id: hit.id,
      from: input.world,
      start: entity.source.rotation,
    };
    return { selection: [hit.id], cursor: 'grabbing' };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (this.state.name !== 'turning') {
      const hit = hitTest(context.scene, input.world, context.tolerance, {
        filter: (_id, kind) => kind === 'solid',
      });
      return { cursor: hit === null ? 'default' : 'grab' };
    }

    const delta = vec.subtract(input.world, this.state.from);
    const rotation = {
      // Screen y runs up in world space, so a downward drag pitches the top away.
      x: this.state.start.x - delta.y * RADIANS_PER_UNIT,
      y: this.state.start.y + delta.x * RADIANS_PER_UNIT,
      z: this.state.start.z,
    };

    return {
      actions: [{ kind: 'set-solid', ids: [this.state.id], patch: { rotation } }],
      label: 'Xoay khối',
      mergeKey: `rotate:${this.state.id}`,
      cursor: 'grabbing',
    };
  }

  onPointerUp(): ToolResponse | null {
    if (this.state.name !== 'turning') return null;
    this.state = { name: 'idle' };
    // Sealing ends the gesture, so the next turn is a separate undo step.
    return { sealHistory: true, cursor: 'grab' };
  }

  cancel(): ToolResponse | null {
    if (this.state.name !== 'idle') this.state = { name: 'idle' };
    return null;
  }

  reset(): void {
    this.state = { name: 'idle' };
  }
}

/**
 * How far a drag turns the solid.
 *
 * A full turn takes roughly 360 world units of drag, which at default zoom is most of the
 * canvas width. Faster than that and a solid spins past the orientation the user wanted;
 * slower and turning it over becomes several drags.
 */
const RADIANS_PER_UNIT = Math.PI / 180;
